import { Icons, Input, Skeleton } from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import { useMemo, useState } from 'react';
import {
  DocumentAssemblerVariableCard,
  type IDocumentAssemblerVariableCardAction,
  type IDocumentAssemblerVariableCardInlineAction,
} from './DocumentAssemblerVariableCard';
import type { IDocumentAssemblerVariableGroup } from './DocumentAssemblerVariableHelper';
import {
  DocumentAssemblerVariableHelper,
  IDocumentAssemblerVariable,
} from './DocumentAssemblerVariableHelper';

interface IDocumentAssemblerVariableRailProps {
  title?: string;
  groups: IDocumentAssemblerVariableGroup[];
  isLoading: boolean;
  showSearch?: boolean;
  /** Confirm step: show the occurrence count on its own line rather than tucked inside the token chip. */
  showMatchCountLine?: boolean;
  actionsFor?: (
    variable: IDocumentAssemblerVariable
  ) => IDocumentAssemblerVariableCardAction[];
  inlineActionFor?: (
    variable: IDocumentAssemblerVariable
  ) => IDocumentAssemblerVariableCardInlineAction | undefined;
  footer?: React.ReactNode;
  className?: string;
}

export const DocumentAssemblerVariableRail = ({
  title,
  groups,
  isLoading,
  showSearch,
  showMatchCountLine,
  actionsFor,
  inlineActionFor,
  footer,
  className,
}: IDocumentAssemblerVariableRailProps) => {
  const [search, setSearch] = useState('');

  const visibleGroups = useMemo(
    () =>
      groups
        .map((group) => ({
          ...group,
          variables: group.variables.filter((variable) =>
            DocumentAssemblerVariableHelper.matchesSearch(variable, search)
          ),
        }))
        .filter((group) => group.variables.length > 0),
    [groups, search]
  );

  const isEmpty = !isLoading && visibleGroups.length === 0;

  return (
    <aside
      aria-busy={isLoading}
      className={cn('flex min-h-0 flex-col bg-bg-primary', className)}
    >
      {title ? (
        <header className="shrink-0 border-b border-border-secondary px-xl py-lg">
          <h2 className="txt-small-medium text-text-primary">{title}</h2>
        </header>
      ) : null}

      {showSearch ? (
        <div className="shrink-0 px-xl pb-md pt-lg">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Search variables"
            placeholder="Search variables"
            fullWidth
            leftEl={() => (
              <Icons.SearchMd className="size-4 text-fg-tertiary" />
            )}
          />
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col gap-2xl overflow-y-auto p-xl">
        {isLoading ? (
          <div className="flex flex-col gap-lg">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-[93px] w-full rounded-xl" />
            <Skeleton className="h-[93px] w-full rounded-xl" />
            <Skeleton className="h-[93px] w-full rounded-xl" />
          </div>
        ) : null}

        {isEmpty ? (
          <p className="txt-small-regular text-text-tertiary">
            {search.trim()
              ? 'No variable matches that search.'
              : 'This template has no variables yet.'}
          </p>
        ) : null}

        {visibleGroups.map((group) => (
          <section key={group.key} className="flex flex-col gap-md">
            <h3 className="px-xxs txt-extra-small-medium text-text-primary">
              {group.name}
            </h3>
            <div className="flex flex-col gap-md">
              {group.variables.map((variable) => (
                <DocumentAssemblerVariableCard
                  key={variable.instruction}
                  label={variable.label}
                  instruction={variable.instruction}
                  count={variable.count}
                  showMatchCountLine={showMatchCountLine}
                  actions={actionsFor?.(variable)}
                  inlineAction={inlineActionFor?.(variable)}
                />
              ))}
            </div>
          </section>
        ))}
      </div>

      {footer ? (
        <footer className="flex shrink-0 items-center justify-end gap-md border-t border-border-secondary px-xl py-lg">
          {footer}
        </footer>
      ) : null}
    </aside>
  );
};
